const BasePlugin = require('./basePlugin');
const edit = require('../../layout/components/edit');
const loaders = require('../../layout/components/loaders');
const { createTempSvgElement, addLocationMetaInfoToSVGElement, processGeoJSON, getFeatureUid } = require('../mapUtils');

const leafletGeoman = require('@geoman-io/leaflet-geoman-pro');
const leafletGeomanStyle = require('@geoman-io/leaflet-geoman-pro/dist/leaflet-geoman.css');

L.PM.setOptIn(true);

class EditModePlugin extends BasePlugin {
  constructor(ctrl) {
    super(ctrl);
    this.editLayer = null;
    this._activeRecord = null;
    this._originalGeojson = null;
    this._hasPendingChanges = false;
    this._skipNextSave = false;
    this._vertexMode = false;
    this._dragMode = false;
    this._removeMode = false;
    this._rotateMode = false;
    this._scaleMode = false;
    this._drawMode = false;
    this._pendingReset = false;
    this._skipUntilNextChange = false;
    this._finishRequested = false;
    this._cancelRequested = false;
    this._restoreDisplayTimers = [];
    this._removalClickHandler = (e) => {
      if (!this._removeMode) return;
      if (e?.target) {
        this.logDebug('Removal click', { targetId: e.target._leaflet_id });
        this.pruneLayerFromEditOverlay(e.target);
        this.handleChange();
      }
    };
    this._onGlobalEditModeToggled = (e) => this.handleGlobalModeToggle(e, 'edit');
    this._onGlobalDragModeToggled = (e) => this.handleGlobalModeToggle(e, 'drag');
    this._onGlobalRotateModeToggled = (e) => this.handleGlobalModeToggle(e, 'rotate');
    this._onGlobalScaleModeToggled = (e) => this.handleGlobalModeToggle(e, 'scale');
    this._onGlobalRemovalModeToggled = (e) => this.handleGlobalModeToggle(e, 'remove');
    this._rotateMode = false;

    this.ctrl.eventBus.once('mithril:oninit', () => {
      edit.on('toggle', this.handleEditModeToggle);
    });

    this.ctrl.eventBus.once('mithril:oncreate', () => {
      this.ctrl.map.options.pmIgnore = false;
      L.PM.reInitLayer(this.ctrl.map);

      this.ctrl.map.pm.Draw.Marker.setOptions({
        markerStyle: {
          icon: new L.icon({
            iconUrl: '/images/marker-icon.png',
            shadowUrl: '/images/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
          }),
        },
      });

      if (edit.vm.editing()) {
        this.ctrl.map.whenReady(() => this.handleEditModeActivation());
      }
    });

    this.ctrl.eventBus.once('mithril:onremove', () => {
      this.cleanUp();
    });
  }

  cleanUp = () => {
    edit.off('toggle', this.handleEditModeToggle);
    this.clearScheduledDisplayLayerRestores();
    this.teardownEditLayer();
  };

  handleEditModeToggle = () => {
    if (edit.vm.editing()) {
      this.handleEditModeActivation();
    } else {
      this.handleEditModeDeactivation();
    }
  };

  getActiveRecord = () => {
    if (this.ctrl?.equipmentRecord && this.ctrl.equipmentRecord()) {
      return { record: this.ctrl.equipmentRecord(), context: 'child' };
    }
    if (this.ctrl?.location) {
      return { record: this.ctrl.location(), context: 'this' };
    }
    return { record: null, context: null };
  };

  handleEditModeActivation = () => {
    this.clearScheduledDisplayLayerRestores();
    const { record, context } = this.getActiveRecord();
    if (!record || record['@class'] === 'Dashboard') return;

    this._activeRecord = { record, context };
    this._originalGeojson = typeof record.geojson === 'string' ? record.geojson : JSON.stringify(record.geojson || null);
    this._hasPendingChanges = false;
    this._skipNextSave = false;
    this._skipUntilNextChange = false;
    this._finishRequested = false;
    this._cancelRequested = false;
    this._pendingReset = false;

    this.ctrl.map.pm.addControls({
      position: 'topleft',
      drawCircleMarker: false,
      drawMarker: false,
      rotateMode: true,
      scaleMode: true,
      cutPolygon: true,
      mergePolygons: true,
      unionMode: true,
      differenceMode: true,
      removalMode: true,
      drawPolyline: true,
      drawPolygon: true,
      drawRectangle: true,
      drawCircle: false,
      drawText: false,
      editMode: true,
      dragMode: true,
      splitMode: true,
      optionsControls: true,
      snappingOption: true,
      snapGuidesOption: true,
    });
    this.applyGlobalSnapOptions();

    this.createEditLayer();
    this.removeDisplayCopy();
    this.scheduleDisplayLayerRestore([0, 100], { skipActiveRecord: true, adjustViewport: false });

    // Clear any default edit handles that Geoman might show on init
    setTimeout(() => {
      try {
        this.ctrl.map.pm.disableGlobalEditMode();
        this.ctrl.map.pm.disableGlobalDragMode();
      } catch (_) {}
      this.setLayerInteractivity(false, false, false, false, false);
    }, 0);

    const editableLayer = this.buildEditableLayer(record, context);
    if (editableLayer) {
      this.adoptEditableLayer(editableLayer);
      this.setLayerInteractivity(false, false, false, false, false);
    }

    this.ctrl.map.on('pm:create', this.handleCreate);
    this.ctrl.map.on('pm:remove', this.handleRemove);
    this.ctrl.map.on('pm:cut', this.handleCut);
    this.ctrl.map.on('pm:buttonclick', this.handleButtonClick);
    this.ctrl.map.on('pm:actionclick', this.handleToolbarAction);
    this.ctrl.map.on('pm:cancel', this.handleCancel);
    this.ctrl.map.on('pm:globaleditmodetoggled', this._onGlobalEditModeToggled);
    this.ctrl.map.on('pm:globaldrawmodetoggled', this.handleGlobalDrawToggle);
    this.ctrl.map.on('pm:globaldragmodetoggled', this._onGlobalDragModeToggled);
    this.ctrl.map.on('pm:globalrotatemodetoggled', this._onGlobalRotateModeToggled);
    this.ctrl.map.on('pm:globalscalemodetoggled', this._onGlobalScaleModeToggled);
    this.ctrl.map.on('pm:globalremovalmodetoggled', this._onGlobalRemovalModeToggled);
  };

  handleEditModeDeactivation = () => {
    this.clearScheduledDisplayLayerRestores();
    this.ctrl.map.pm.removeControls();
    try {
      this.ctrl.map.off('pm:create', this.handleCreate);
      this.ctrl.map.off('pm:remove', this.handleRemove);
      this.ctrl.map.off('pm:cut', this.handleCut);
      this.ctrl.map.off('pm:buttonclick', this.handleButtonClick);
      this.ctrl.map.off('pm:actionclick', this.handleToolbarAction);
      this.ctrl.map.off('pm:cancel', this.handleCancel);
      this.ctrl.map.off('pm:globaleditmodetoggled', this._onGlobalEditModeToggled);
      this.ctrl.map.off('pm:globaldrawmodetoggled', this.handleGlobalDrawToggle);
      this.ctrl.map.off('pm:globaldragmodetoggled', this._onGlobalDragModeToggled);
      this.ctrl.map.off('pm:globalrotatemodetoggled', this._onGlobalRotateModeToggled);
      this.ctrl.map.off('pm:globalscalemodetoggled', this._onGlobalScaleModeToggled);
      this.ctrl.map.off('pm:globalremovalmodetoggled', this._onGlobalRemovalModeToggled);
    } catch (_) {}

    this.teardownEditLayer();
    this.restoreDisplayLayers();
    this.orderLocationDisplayLayers();
    this.scheduleLocationLayerOrdering([0, 100, 300]);
    this._activeRecord = null;
    this._vertexMode = false;
    this._dragMode = false;
    this._removeMode = false;
    this._rotateMode = false;
    this._scaleMode = false;
    this._drawMode = false;
    this._pendingReset = false;
    this._skipUntilNextChange = false;
    this._finishRequested = false;
    this._cancelRequested = false;
    this._skipNextSave = false;
  };

  createEditLayer = () => {
    if (this.editLayer && this.ctrl?.map?.hasLayer && this.ctrl.map.hasLayer(this.editLayer)) {
      this.ctrl.map.removeLayer(this.editLayer);
    }
    if (this.ctrl?.map?.createPane) {
      const pane = this.ctrl.map.getPane('pane-editing') || this.ctrl.map.createPane('pane-editing');
      if (pane) pane.style.zIndex = '450'; // keep below Geoman handle markers
    }
    this.editLayer = L.featureGroup([], { pane: 'pane-editing', pmIgnore: false });
    this.editLayer.addTo(this.ctrl.map);
    try {
      const labelsPane = this.ctrl.map.getPane('pane-labels');
      if (labelsPane) labelsPane.style.pointerEvents = 'none';
    } catch (_) {}
  };

  teardownEditLayer = () => {
    try {
      const labelsPane = this.ctrl?.map?.getPane && this.ctrl.map.getPane('pane-labels');
      if (labelsPane) labelsPane.style.pointerEvents = '';
    } catch (_) {}

    if (this.editLayer) {
      try { this.editLayer.off(); } catch (_) {}
      try { this.ctrl.map.removeLayer(this.editLayer); } catch (_) {}
    }
    this.editLayer = null;
  };

  buildEditableLayer = (record, context) => {
    if (!record || !record.geojson) return null;
    const rid = record['@rid'] ? record['@rid'].toString().slice(1) : null;
    const recordWithContext = Object.assign({}, record, { context: record.context || context });
    const layer = processGeoJSON(recordWithContext, rid, (this.ctrl.locationConfig && this.ctrl.locationConfig().thisStyle) || {});
    this.setPaneRecursively(layer, 'pane-editing');
    this.applyRecordStyle(layer, recordWithContext);
    return layer;
  };

  setPaneRecursively = (layer, paneName) => {
    if (!layer) return;
    if (layer.eachLayer) {
      layer.eachLayer(child => this.setPaneRecursively(child, paneName));
    }
    if (layer.options) {
      layer.options.pane = paneName;
      if (typeof layer.redraw === 'function') {
        try { layer.redraw(); } catch (_) {}
      }
    }
  };

  setLayerPmSnapOptions = (layer) => {
    const snapTargets = this.getSnapTargetLayers();
    const apply = (l) => {
      if (!l) return;
      if (l.eachLayer) {
        l.eachLayer(apply);
      }
      if (l.pm && l.pm.setOptions) {
        try {
          l.pm.setOptions({
            snappable: true,
            snapSegment: true,
            snapDrag: true,
            snapMiddle: true,
            snapDistance: 20,
            snapToSelf: true,
            allowSelfIntersection: false,
            snapLayers: snapTargets,
          });
        } catch (_) {}
      }
    };
    apply(layer);
    this.applyGlobalSnapOptions(snapTargets);
  };

  getSnapTargetLayers = () => {
    const targets = [];
    const collect = (l) => {
      if (!l) return;
      const isLeafGeometry = typeof l.getLatLngs === 'function' || typeof l.getLatLng === 'function';
      if (!isLeafGeometry && l.eachLayer) {
        l.eachLayer(collect);
        return;
      }
      if (!isLeafGeometry) return;
      if (l?.feature?.properties?.name === 'Bounding_Box') return;
      targets.push(l);
    };
    if (this.editLayer) collect(this.editLayer);
    return targets;
  };

  adoptEditableLayer = (layer) => {
    if (!layer) return;
    this.setPaneRecursively(layer, 'pane-editing');
    this.setPmAccessibility(layer, { pmIgnore: false, snapIgnore: false });
    this.applyNewFeatureStyle(layer);
    try { this.editLayer && this.editLayer.addLayer(layer); } catch (_) {}
    this.enableEditingOnLayer(layer);
  };

  setPmAccessibility = (layer, { pmIgnore = false, snapIgnore = false } = {}) => {
    const apply = (l) => {
      if (!l) return;
      const isLeafGeometry = typeof l.getLatLngs === 'function' || typeof l.getLatLng === 'function';
      if (!isLeafGeometry && l.eachLayer) {
        l.eachLayer(apply);
        return;
      }
      if (!isLeafGeometry) return;
      try { l.options.pmIgnore = pmIgnore; } catch (_) {}
      try { l.options.snapIgnore = snapIgnore; } catch (_) {}
      try { L.PM.reInitLayer(l); } catch (_) {}
    };
    apply(layer);
  };

  applyGlobalSnapOptions = (snapLayers) => {
    const layers = Array.isArray(snapLayers) ? snapLayers : this.getSnapTargetLayers();
    try {
      this.ctrl.map.pm.setGlobalOptions({
        snappable: true,
        snapToSelf: true,
        snapSegment: true,
        snapDrag: true,
        snapMiddle: true,
        snapDistance: 20,
        allowSelfIntersection: false,
        layerGroup: this.editLayer,
        snapLayers: layers,
        panes: { layerPane: 'pane-editing' },
      });
    } catch (_) {}
    try {
      if (this.ctrl.map.pm.Draw.Split && this.ctrl.map.pm.Draw.Split.setOptions) {
        this.ctrl.map.pm.Draw.Split.setOptions({
          snappable: true,
          snapDrag: true,
          snapSegment: true,
          snapMiddle: true,
          snapDistance: 20,
          allowSelfIntersection: false,
          layerGroup: this.editLayer,
          snapLayers: layers,
        });
      }
    } catch (_) {}
  };

  enableEditingOnLayer = (layer) => {
    const isLeafGeometry = typeof layer.getLatLngs === 'function' || typeof layer.getLatLng === 'function';
    if (!isLeafGeometry && layer.eachLayer) {
      layer.eachLayer(child => this.enableEditingOnLayer(child));
      return;
    }
    if (!isLeafGeometry) return;
    if (layer?.feature?.properties?.name === 'Bounding_Box') {
      this.setPmAccessibility(layer, { pmIgnore: true, snapIgnore: true });
      return;
    }

    try { layer.options && (layer.options.zIndex = 200); } catch (_) {}
    this.setPmAccessibility(layer, { pmIgnore: false, snapIgnore: false });
    this.setLayerPmSnapOptions(layer);

    layer.on('pm:update', this.handleChange);
    layer.on('pm:edit', this.handleChange);
    layer.on('pm:change', this.handleChange);
    layer.on('pm:dragend', this.handleChange);
    layer.on('pm:scaleend', this.handleChange);
    layer.on('pm:cut', this.handleChange);
    layer.on('pm:remove', this.handleRemove);
    layer.on('pm:cancel', this.handleCancel);
    layer.on('pm:rotate', this.handleChange);
    layer.on('pm:rotateend', this.handleChange);
  };

  applyNewFeatureStyle = (layer) => {
    if (!layer?.setStyle) return;
    const activeRecord = this._activeRecord?.record || this.ctrl?.equipmentRecord?.() || this.ctrl?.location?.();
    const style = this.computeRecordStyle(activeRecord);
    layer.setStyle(style);
  };

  handleCreate = (e) => {
    if (!this.editLayer || !e?.layer) return;
    this.logDebug('pm:create fired', { shape: e?.shape, layerId: e?.layer?._leaflet_id });
    this.adoptEditableLayer(e.layer);
    this.logDebug('post-create layer count', { count: this.editLayer.getLayers().length });
    this.handleChange();
    // Save immediately on create events
    this.deferSave();
  };

  handleRemove = (e) => {
    if (e?.layer) {
      this.logDebug('pm:remove received', { layerId: e.layer._leaflet_id });
      this.pruneLayerFromEditOverlay(e.layer);
    }
    this.applyGlobalSnapOptions();
    this.handleChange();
  };

  handleCut = (e) => {
    this.logDebug('pm:cut received', {
      originalLayerId: e?.originalLayer?._leaflet_id,
      layerId: e?.layer?._leaflet_id,
    });

    // Geoman replaces layers on cut; update references and re-wire the result layer(s)
    const originalLayer = e?.originalLayer;
    if (originalLayer) {
      this.pruneLayerFromEditOverlay(originalLayer);
    }

    const resultLayers = [];
    if (e?.layer) resultLayers.push(e.layer);

    // Some versions return a LayerGroup in `e.layers`
    if (e?.layers?.eachLayer) {
      e.layers.eachLayer((l) => resultLayers.push(l));
    } else if (Array.isArray(e?.layers)) {
      resultLayers.push(...e.layers);
    }

    resultLayers.forEach((layer) => {
      if (!layer || !this.editLayer) return;
      this.adoptEditableLayer(layer);
    });

    this.applyGlobalSnapOptions();
    this.handleChange();
    this.deferSave();
  };

  pruneLayerFromEditOverlay = (target) => {
    if (!target) return;
    const pruneFromGroup = (group) => {
      if (!group || !group.eachLayer) return false;
      let removed = false;
      const toRemove = [];
      group.eachLayer(child => {
        if (child === target) {
          toRemove.push(child);
          removed = true;
          return;
        }
        if (child.eachLayer && pruneFromGroup(child)) {
          removed = true;
        }
      });
      toRemove.forEach(child => {
        try { group.removeLayer(child); } catch (_) {}
      });
      return removed;
    };
    pruneFromGroup(this.editLayer);
  };

  computeRecordStyle = (record) => {
    if (!record) return this.defaultEditStyle();
    const style = this.defaultEditStyle();
    if (record.mapPolygonFillColor) style.fillColor = record.mapPolygonFillColor;
    if (record.mapPolygonStrokeColor) style.color = record.mapPolygonStrokeColor;
    if (record.mapPolygonOpacity !== undefined) style.fillOpacity = Number(record.mapPolygonOpacity);
    if (record.mapPolygonStrokeOpacity !== undefined) style.opacity = Number(record.mapPolygonStrokeOpacity);
    if (record.mapPolygonStrokeWidth !== undefined) style.weight = Number(record.mapPolygonStrokeWidth);
    return style;
  };

  applyRecordStyle = (layer, record) => {
    const style = this.computeRecordStyle(record);
    const applyTo = (l) => {
      const isLeafGeometry = typeof l.getLatLngs === 'function' || typeof l.getLatLng === 'function';
      if (!isLeafGeometry && l.eachLayer) {
        l.eachLayer(child => applyTo(child));
        return;
      }
      if (!isLeafGeometry) return;
      if (l?.feature?.properties?.name === 'Bounding_Box') return;
      if (l.setStyle) l.setStyle(style);
    };
    applyTo(layer);
  };

  defaultEditStyle = () => ({
    color: '#2196F3',
    weight: 2,
    opacity: 1,
    fillColor: '#2196F3',
    fillOpacity: 0.2,
  });

  handleChange = async () => {
    if (this._cancelRequested || this._pendingReset || this._skipUntilNextChange) {
      this.logDebug('handleChange skipped due to cancel/reset', {
        cancelRequested: this._cancelRequested,
        pendingReset: this._pendingReset,
        skipUntilNextChange: this._skipUntilNextChange,
      });
      return;
    }
    const wasDirty = this._hasPendingChanges === true;
    this._hasPendingChanges = true;
    this._skipNextSave = false;
    this._skipUntilNextChange = false;
    if (!wasDirty) {
      this.logDebug('handleChange', {
        pendingReset: this._pendingReset,
        skipNextSave: this._skipNextSave,
        skipUntilNextChange: this._skipUntilNextChange,
      });
    }
  };

  handleToolbarAction = async (e) => {
    if (!edit.vm.editing()) return;

    const btnName = e?.btnName ?? e?.button?.name ?? e?.button;
    const action = e?.action;
    const actionName = typeof action === 'string' ? action : action?.name;
    const actionLabel = actionName || action?.text || action?.title || '';
    const normalizedAction = String(actionLabel || '').trim().toLowerCase();
    const isEditButton = btnName === 'edit' || btnName === 'editMode';

    this.logDebug('pm:actionclick', { action: actionName || action, btnName, isEditButton });

    const isCancel = actionName === 'cancelMode'
      || (isEditButton && (actionName === 'cancel' || normalizedAction === 'cancel' || normalizedAction.includes('cancel')));
    const isFinish = actionName === 'finishMode'
      || (isEditButton && (
        actionName === 'finish'
        || normalizedAction === 'finish'
        || normalizedAction.includes('finish')
        || normalizedAction.includes('save')
        || normalizedAction.includes('apply')
      ));

    if (isCancel) {
      this._cancelRequested = true;
      this._finishRequested = false;
      this._pendingReset = true;
      this._skipNextSave = true;
      this._skipUntilNextChange = true;
      this.resetToOriginal();
      this._hasPendingChanges = false;
      return;
    }

    if (isFinish) {
      this._finishRequested = true;
      this._cancelRequested = false;
      this.deferSave();
      this._skipNextSave = false;
      this._pendingReset = false;
      this._skipUntilNextChange = false;
      return;
    }

    // Ignore other action clicks (e.g. draw tool finish/cancel)
    if (!isEditButton) return;
  };

  handleCancel = () => {
    this._cancelRequested = true;
    this._finishRequested = false;
    this._pendingReset = true;
    this._skipNextSave = true;
    this._skipUntilNextChange = true;
    this._hasPendingChanges = false;
    this.logDebug('pm:cancel received', {
      pendingReset: this._pendingReset,
      skipNextSave: this._skipNextSave,
      skipUntilNextChange: this._skipUntilNextChange,
      cancelRequested: this._cancelRequested,
    });
    this.resetToOriginal();
  };

  handleButtonClick = (e) => {
    const name = e?.btnName ?? e?.button?.name ?? e?.button;
    const active = e?.button?._active === true;
    this.logDebug('pm:buttonclick', { name, active });
  };

  handleGlobalModeToggle = async (e, mode) => {
    if (mode === 'edit') this._vertexMode = e?.enabled === true;
    if (mode === 'drag') this._dragMode = e?.enabled === true;
    if (mode === 'remove') this._removeMode = e?.enabled === true;
    if (mode === 'rotate') this._rotateMode = e?.enabled === true;
    if (mode === 'scale') this._scaleMode = e?.enabled === true;
    this.logDebug('pm:globalmodetoggled', {
      mode,
      enabled: e?.enabled,
      pendingReset: this._pendingReset,
      skipNextSave: this._skipNextSave,
      skipUntilNextChange: this._skipUntilNextChange,
      hasPendingChanges: this._hasPendingChanges,
      finishRequested: this._finishRequested,
      cancelRequested: this._cancelRequested,
      eventKeys: e ? Object.keys(e) : [],
    });
    this.setLayerInteractivity(
      this._vertexMode || this._removeMode || this._rotateMode,
      this._dragMode,
      this._removeMode,
      this._rotateMode,
      this._scaleMode
    );

    if (e?.enabled === false) {
      if (this._cancelRequested || this._pendingReset) {
        // Keep skip flags set; cancel handler already reset geometry
        this._skipNextSave = true;
        this._skipUntilNextChange = true;
        return;
      }
      if (this._finishRequested) {
        this._finishRequested = false;
        this._skipNextSave = false;
        this._skipUntilNextChange = false;
        return;
      }
      if (this._pendingReset) {
        this.resetToOriginal();
        this._pendingReset = false;
        this._skipNextSave = false;
        this._skipUntilNextChange = false;
        this._hasPendingChanges = false;
        return;
      }
      if (this._skipNextSave) {
        this._skipNextSave = false;
        this._skipUntilNextChange = false;
        return;
      }
      if (this._hasPendingChanges) {
        this.deferSave();
      }
    }
  };

  deferSave = () => {
    setTimeout(async () => {
      this.logDebug('deferSave fired', {
        pendingReset: this._pendingReset,
        skipNextSave: this._skipNextSave,
        skipUntilNextChange: this._skipUntilNextChange,
        hasPendingChanges: this._hasPendingChanges,
        finishRequested: this._finishRequested,
        cancelRequested: this._cancelRequested,
      });
      if (this._cancelRequested || this._pendingReset || this._skipNextSave || this._skipUntilNextChange) {
        this.logDebug('deferSave aborted due to cancel/reset flags');
        return;
      }
      await this.saveGeometry();
      this._hasPendingChanges = false;
      this._finishRequested = false;
      this._cancelRequested = false;
    }, 0);
  };

  handleGlobalDrawToggle = (e) => {
    this._drawMode = e?.enabled === true;
    this.logDebug('global draw toggled', { enabled: this._drawMode });
    // Allow vertex editing while draw is active so shapes place correctly
    if (this._drawMode) {
      this.setLayerInteractivity(true, this._dragMode, this._removeMode, this._rotateMode, this._scaleMode);
    } else {
      this.setLayerInteractivity(
        this._vertexMode || this._removeMode || this._rotateMode,
        this._dragMode,
        this._removeMode,
        this._rotateMode,
        this._scaleMode
      );
    }
  };

  setLayerInteractivity = (enableVertex, enableDrag, enableRemoval = false, enableRotate = false, enableScale = false) => {
    this.logDebug('setLayerInteractivity', {
      enableVertex,
      enableDrag,
      enableRemoval,
      enableRotate,
      drawMode: this._drawMode,
      scaleMode: this._scaleMode,
    });
    const toggleLayer = (layer) => {
      const isLeafGeometry = typeof layer.getLatLngs === 'function' || typeof layer.getLatLng === 'function';
      if (!isLeafGeometry && layer.eachLayer) {
        layer.eachLayer(child => toggleLayer(child));
        return;
      }
      if (!isLeafGeometry) return;

      const shouldAllow = enableVertex || enableDrag || enableRemoval || enableRotate || enableScale || this._drawMode || this._scaleMode;
      this.logDebug('toggle layer', { id: layer._leaflet_id, shouldAllow, pane: layer?.options?.pane });
      try {
        if (layer.pm && layer.pm.enableLayerDrag && layer.pm.disableLayerDrag) {
          if (enableDrag) {
            layer.pm.enableLayerDrag();
          } else {
            layer.pm.disableLayerDrag();
          }
        }
      } catch (_) {}

      try {
        if (layer.pm && layer.pm.enableRotate && layer.pm.disableRotate) {
          if (enableRotate) {
            layer.pm.enableRotate();
          } else {
            layer.pm.disableRotate();
          }
        }
      } catch (_) {}

      // Rely on Geoman's global scale mode; avoid per-layer enable/disable to prevent mode toggle loops

      // Manual removal click binding to ensure delete works even if Geoman doesn't fire pm:remove
      try {
        if (enableRemoval) {
          layer.off('click', this._removalClickHandler);
          layer.on('click', this._removalClickHandler);
        } else {
          layer.off('click', this._removalClickHandler);
        }
      } catch (_) {}
    };

    if (this.editLayer) {
      toggleLayer(this.editLayer);
    }
  };

  getCurrentEditGeoJSON = () => {
    if (this.editLayer) {
      try {
        const geo = this.editLayer.toGeoJSON(9);
        this.logDebug('Collected GeoJSON from editLayer', { featureCount: geo?.features?.length });
        return geo;
      } catch (_) {}
    }

    return null;
  };

  saveGeometry = async () => {
    if (!this.editLayer) return;
    if (this._pendingReset || this._skipNextSave || this._skipUntilNextChange || this._cancelRequested) {
      this.logDebug('Skipping save due to pending reset/skip', {
        pendingReset: this._pendingReset,
        skipNextSave: this._skipNextSave,
        skipUntilNextChange: this._skipUntilNextChange,
        cancelRequested: this._cancelRequested,
      });
      return;
    }
    const { record, context } = this._activeRecord || this.getActiveRecord();
    if (!record) return;

    const currentGeo = this.getCurrentEditGeoJSON();
    this.logDebug('Saving geometry from edit layer', {
      hasEditLayer: Boolean(this.editLayer),
      activeContext: context,
      recordRid: record['@rid'],
      geoLayerSource: currentGeo ? 'resolved' : 'null',
    });
    const geoJSON = this.dedupeGeoJSON(currentGeo);

    // Pick a sensible center even when all features are removed
    let center;
    try {
      const bounds = this.editLayer.getBounds();
      if (bounds && bounds.isValid()) {
        center = bounds.getCenter();
      }
    } catch (_) {}

    if (!center && typeof this.ctrl?.map?.getCenter === 'function') {
      center = this.ctrl.map.getCenter();
    }

    if (!center && record?.latitude && record?.longitude) {
      center = { lat: Number(record.latitude), lng: Number(record.longitude) };
    }

    let latitude = center?.lat;
    let longitude = center?.lng;

    let requestBody = {
      latitude,
      longitude,
      geojson: JSON.stringify(geoJSON),
    };

    const svgString = this.ctrl.location().svg;
    if (svgString) {
      const svgElement = createTempSvgElement(svgString);
      addLocationMetaInfoToSVGElement(svgElement, center);
      const serializer = new XMLSerializer();
      requestBody.svg = serializer.serializeToString(svgElement);
    }

    const requestURL = getRequestUrl(this.ctrl);
    await loaders.requestWithLoader({
      method: 'PUT',
      url: requestURL,
      body: requestBody,
    });

    try {
      record.geojson = requestBody.geojson;
      record.context = context;
      const uid = this.ensureFeatureIdentity(record, context);
      this.ctrl.eventBus.emit('featuresToRemove', [uid]);
      this.ctrl.eventBus.emit('featuresToAdd', [record]);
    } catch (_) {}
  };

  resetToOriginal = () => {
    if (!this.editLayer) return;
    const { record, context } = this._activeRecord || this.getActiveRecord();
    if (!record) return;
    const originalGeojson = this._originalGeojson;
    try {
      this.editLayer.clearLayers();
      if (originalGeojson) {
        const restored = Object.assign({}, record, { geojson: originalGeojson, context: record.context || context });
        const layer = this.buildEditableLayer(restored, context);
        if (layer) {
          this.editLayer.addLayer(layer);
          this.enableEditingOnLayer(layer);
        }
      }
      this.applyGlobalSnapOptions();
    } catch (_) {}
  };

  dedupeGeoJSON = (geojson) => {
    if (!geojson) return geojson;
    const features = geojson.type === 'FeatureCollection'
      ? (Array.isArray(geojson.features) ? geojson.features : [])
      : [geojson];

    const seen = new Set();
    const dedupedFeatures = [];

    features.forEach(feature => {
      if (!feature) return;
      try {
        const key = JSON.stringify(feature);
        if (seen.has(key)) return;
        seen.add(key);
        dedupedFeatures.push(feature);
      } catch (_) {}
    });

    if (geojson.type === 'FeatureCollection') {
      return Object.assign({}, geojson, { features: dedupedFeatures });
    }

    if (dedupedFeatures.length === 1) return dedupedFeatures[0];

    return {
      type: 'FeatureCollection',
      features: dedupedFeatures,
    };
  };

  logDebug = (...args) => {
    try {
      console.log('[EditMode]', ...args);
    } catch (_) {}
  };

  removeDisplayCopy = () => {
    const { record, context } = this._activeRecord || this.getActiveRecord();
    if (!record || typeof this.ctrl?.getFeatureUid !== 'function') return;
    try {
      const uid = this.ensureFeatureIdentity(record, context);
      this.ctrl.eventBus.emit('featuresToRemove', [uid]);
    } catch (_) {}
  };

  restoreDisplayCopy = () => {
    const { record, context } = this._activeRecord || this.getActiveRecord();
    if (!record) return;
    try {
      this.ensureFeatureIdentity(record, context);
      this.ctrl.eventBus.emit('featuresToAdd', [record]);
    } catch (_) {}
  };

  restoreDisplayLayers = (options = {}) => {
    if (!this.ctrl?.eventBus) {
      this.restoreDisplayCopy();
      return;
    }

    const features = this.getCurrentDisplayFeatures();
    if (!features.length) {
      this.restoreDisplayCopy();
      return;
    }

    const missingFeatures = [];
    const missingUids = [];
    const activeUid = options.skipActiveRecord ? this.getActiveRecordUid() : null;
    features.forEach(feature => {
      const uid = this.ensureFeatureIdentity(feature, feature.context);
      if (activeUid && uid === activeUid) return;
      if (!uid || this.hasDisplayLayer(uid)) return;
      missingFeatures.push(feature);
      missingUids.push(uid);
    });

    if (missingFeatures.length) {
      this.ctrl.eventBus.emit('featuresToRemove', missingUids);
      this.ctrl.eventBus.emit('featuresToAdd', missingFeatures);
    }

    this.orderLocationDisplayLayers();

    try {
      this.ctrl.map?.invalidateSize();
      if (options.adjustViewport !== false) {
        this.ctrl.setViewportAndAdjustBounds && this.ctrl.setViewportAndAdjustBounds({ deferFallback: true });
      }
    } catch (_) {}
  };

  scheduleDisplayLayerRestore = (delays, options = {}) => {
    const restoreDelays = Array.isArray(delays) && delays.length ? delays : [0];
    restoreDelays.forEach(delay => {
      const timer = setTimeout(() => {
        this._restoreDisplayTimers = this._restoreDisplayTimers.filter(id => id !== timer);
        if (!this.ctrl?.map) return;
        this.restoreDisplayLayers(options);
      }, delay);
      this._restoreDisplayTimers.push(timer);
    });
  };

  clearScheduledDisplayLayerRestores = () => {
    this._restoreDisplayTimers.forEach(timer => clearTimeout(timer));
    this._restoreDisplayTimers = [];
  };

  scheduleLocationLayerOrdering = (delays) => {
    const orderDelays = Array.isArray(delays) && delays.length ? delays : [0];
    orderDelays.forEach(delay => {
      const timer = setTimeout(() => {
        this._restoreDisplayTimers = this._restoreDisplayTimers.filter(id => id !== timer);
        if (!this.ctrl?.map) return;
        this.orderLocationDisplayLayers();
      }, delay);
      this._restoreDisplayTimers.push(timer);
    });
  };

  getCurrentDisplayFeatures = () => {
    if (typeof this.ctrl?.getCurrentFeatures !== 'function') return [];
    try {
      return (this.ctrl.getCurrentFeatures() || []).filter(Boolean);
    } catch (_) {
      return [];
    }
  };

  getActiveRecordUid = () => {
    const { record, context } = this._activeRecord || this.getActiveRecord();
    if (!record) return null;
    return this.ensureFeatureIdentity(record, context);
  };

  orderLocationDisplayLayers = () => {
    const locationLayer = this.ctrl?.locationParentLayer;
    if (!locationLayer || typeof locationLayer.getLayers !== 'function') return;
    const orderedUids = this.getCurrentDisplayFeatures()
      .map(feature => this.ensureFeatureIdentity(feature, feature.context))
      .filter(Boolean);

    orderedUids.forEach(uid => {
      const matchingLayers = locationLayer.getLayers().filter(layer => layer?.UID === uid);
      matchingLayers.forEach(layer => {
        try {
          if (typeof layer.bringToFront === 'function') layer.bringToFront();
        } catch (_) {}
      });
    });
  };

  ensureFeatureIdentity = (feature, fallbackContext) => {
    if (!feature) return null;
    const record = feature.in ? feature.in : feature;
    const context = fallbackContext || feature.context || record?.context;
    if (context) feature.context = context;
    if (context && record) record.context = context;
    const getUid = typeof this.ctrl?.getFeatureUid === 'function' ? this.ctrl.getFeatureUid : getFeatureUid;
    const uid = getUid(feature);
    if (uid && !feature.UID) feature.UID = uid;
    return uid;
  };

  hasDisplayLayer = (uid) => {
    if (!uid || !Array.isArray(this.ctrl?.allLayers)) return false;
    return this.ctrl.allLayers.some(layer => {
      if (!layer || layer.UID !== uid) return false;
      if (!this.isDisplayLayerAttached(layer)) return false;
      if (typeof layer.getLayers === 'function') return true;
      if (typeof layer.setStyle === 'function') return true;
      if (layer._quickviewRecord) return true;
      return false;
    });
  };

  isDisplayLayerAttached = (layer) => {
    const parents = [
      this.ctrl?.locationParentLayer,
      this.ctrl?.equipmentParentLayer,
      this.ctrl?.zoneParentLayer,
      this.ctrl?.markerClusterGroup,
    ];
    try {
      if (parents.some(parent => parent?.hasLayer && parent.hasLayer(layer))) return true;
      if (this.ctrl?.map?.hasLayer && this.ctrl.map.hasLayer(layer)) return true;
    } catch (_) {}
    return false;
  };
}

function getRequestUrl(ctrl) {
  return (ctrl?.equipmentRecord && ctrl.equipmentRecord())
    ? `/api/v1/equipment/${ctrl.equipmentRecord()['@rid'].slice(1)}`
    : `/api/v1/${ctrl.location()['@class'].toLowerCase()}/${ctrl.location()['@rid'].slice(1)}`;
}

module.exports = EditModePlugin;
